/**
 * ============================================================================
 * ANALYTICS PAGE
 * ============================================================================
 *
 * User analytics dashboard – green/black theme matching sidebar and dashboard
 */

import React, { useState, useRef, useEffect } from 'react';
import { UserDashboardLayout } from '../components/user';
import { useI18n } from '../providers';
import {
  TransactionAnalytics,
  WalletAnalytics,
  PaymentAnalytics,
  DisputeAnalytics,
  ActivityTimeline,
  AnalyticsOverview,
  ExportAnalytics,
} from '../components/analytics';
import '../styles/landing.css';
import '../styles/animations.css';
import '../styles/global.css';

const Analytics = () => {
  const { language } = useI18n();
  const [activeTab, setActiveTab] = useState('overview');
  const [contentInView, setContentInView] = useState(false);
  const contentRef = useRef(null);

  const tabs = [
    {
      id: 'overview',
      label: language === 'fr' ? "Vue d'ensemble" : 'Overview',
      icon: '📊',
      desc: language === 'fr' ? 'Métriques clés' : 'Key metrics',
    },
    {
      id: 'transactions',
      label: language === 'fr' ? 'Transactions' : 'Transactions',
      icon: '📦',
      desc: language === 'fr' ? 'Volume & tendances' : 'Volume & trends',
    },
    {
      id: 'wallet',
      label: language === 'fr' ? 'Portefeuille' : 'Wallet',
      icon: '💰',
      desc: language === 'fr' ? 'Solde & mouvements' : 'Balance & movements',
    },
    {
      id: 'payments',
      label: language === 'fr' ? 'Paiements' : 'Payments',
      icon: '💳',
      desc: language === 'fr' ? 'Historique paiements' : 'Payment history',
    },
    {
      id: 'disputes',
      label: language === 'fr' ? 'Litiges' : 'Disputes',
      icon: '⚠️',
      desc: language === 'fr' ? 'Résolutions' : 'Resolutions',
    },
    {
      id: 'activity',
      label: language === 'fr' ? 'Activité' : 'Activity',
      icon: '📈',
      desc: language === 'fr' ? 'Timeline récente' : 'Recent timeline',
    },
  ];

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => e.isIntersecting && setContentInView(true)); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab]);

  const getCurrentFilters = () => ({ timeRange: '30d' });
  const showExport = activeTab !== 'overview' && activeTab !== 'activity';

  return (
    <UserDashboardLayout>
      <div className="analytics-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              📊 {language === 'fr' ? 'Analytique' : 'Analytics'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Analysez vos performances et suivez vos statistiques en temps réel'
                : 'Analyze your performance and track your statistics in real-time'}
            </p>
            {showExport && (
              <div className="analytics-wt-hero-export mt-6">
                <ExportAnalytics
                  analyticsType={activeTab}
                  filters={getCurrentFilters()}
                />
              </div>
            )}
          </div>
        </section>

        {/* Tabs - same style as Profile */}
        <section className="settings-wt-section profile-wt-tabs-wrap">
          <div className="settings-wt-section-inner">
            <div className="profile-wt-tabs" role="tablist">
              {tabs.map((tab, i) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`profile-wt-tab ${activeTab === tab.id ? 'profile-wt-tab-active' : ''}`}
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <span className="profile-wt-tab-icon">{tab.icon}</span>
                  <span className="profile-wt-tab-label">{tab.label}</span>
                  <span className="profile-wt-tab-desc">{tab.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tab content */}
        <section
          className={`settings-wt-section dashboard-wt-section-alt analytics-wt-content ${contentInView ? 'is-inview' : ''}`}
          ref={contentRef}
        >
          <div className="settings-wt-section-inner">
            {activeTab === 'overview' && <AnalyticsOverview />}
            {activeTab === 'transactions' && <TransactionAnalytics />}
            {activeTab === 'wallet' && <WalletAnalytics />}
            {activeTab === 'payments' && <PaymentAnalytics />}
            {activeTab === 'disputes' && <DisputeAnalytics />}
            {activeTab === 'activity' && <ActivityTimeline />}
          </div>
        </section>
      </div>
    </UserDashboardLayout>
  );
};

export default Analytics;
